/**
 * Integration Listing Service
 *
 * Isolates all network I/O for the IntegrationListing feature.
 * Components and hooks should call these functions rather than
 * calling `callApi` directly, keeping API concerns out of UI logic.
 */

import callApi from 'lib/callApi';

import {
  IntegrationListingResponse,
  IntegrationQueryParams,
} from '../types/integrationListing.types';

/** GET /bpa/integrations/ — fetch paginated integration records */
export async function fetchIntegrations(
  params: IntegrationQueryParams,
): Promise<IntegrationListingResponse> {
  const response = await callApi('bpa/integrations/', 'GET', params);
  return response.data as IntegrationListingResponse;
}

/** GET /bpa/integrations/options/ — fetch integration options */
export async function fetchIntegrationOptions(
  companyId: string | number,
  models: string,
) {
  return callApi('bpa/integrations/options/', 'GET', {
    company_id: companyId,
    models: models, // e.g. 'providers,modules,regions'
  });
}

/** PATCH /bpa/integrations/:id/ — update integration label */
export async function updateIntegrationLabel(
  id: number,
  label: string,
  companyId: string | number,
) {
  return callApi(
    `bpa/integrations/${id}/`,
    'PATCH',
    {},
    { company_id: companyId, integration_label: label },
  );
}

/** DELETE /bpa/integrations/:id/delete/ — delete integration */
export async function deleteIntegration(
  id: number,
  companyId: string | number,
) {
  return callApi(`bpa/integrations/${id}/delete/`, 'DELETE', {
    company_id: companyId,
  });
}

/** PATCH /bpa/integrations/manual_scan/:id/ — initiate manual scan */
export async function manualScan(
  id: number,
  companyId: string | number,
  region: string,
  benchmark: string[],
) {
  return callApi(
    `bpa/integrations/manual_scan/${id}/`,
    'PATCH',
    {},
    { company_id: companyId, region, benchmark },
  );
}

/** PATCH /bpa/integrations/cancel_manual_scan/:id/ — cancel manual scan */
export async function cancelManualScan(id: number, companyId: string | number) {
  return callApi(
    `bpa/integrations/cancel_manual_scan/${id}/`,
    'PATCH',
    {},
    { company_id: companyId },
  );
}

/** POST /bpa/new-integrations/ — create a new integration */
export async function createIntegration(payload: Record<string, unknown>) {
  return callApi('bpa/new-integrations/', 'POST', {}, payload);
}

/** GET /bpa/integrations/:id/status/ — check provisioning status */
export async function checkProvisioningStatus(
  id: string | number,
  companyId: string | number,
) {
  return callApi(`bpa/integrations/${id}/status/`, 'GET', {
    company_id: companyId,
  });
}

/** GET /bpa/integrations/insights/ — fetch aggregated insights */
export async function fetchIntegrationInsights(params: IntegrationQueryParams) {
  return callApi('bpa/integrations/insights/', 'GET', params);
}

/** GET /bpa/integrations/:id/ — get integration details */
export async function fetchIntegrationDetails(
  id: number,
  companyId: string | number,
) {
  return callApi(`bpa/integrations/${id}/?company_id=${companyId}`, 'GET', {});
}
